/*
  Visually groups the Solid Cologne Bundle's real per-scent cart lines into a
  single card in Zipify One Click Upsell's Slide Cart Drawer, so the bundle
  reads as "one deal" even though each scent is a real, separately-tracked
  line item (needed for accurate inventory/Klaviyo/Triple Whale data — see
  assets/bundle-buy-box.js). Lines are grouped by their shared
  properties['_Bundle Id'] value, written by the bundle buy box on add-to-cart.

  OCU renders its own drawer independently of this theme: this script reads
  real cart data from /cart.js, hides the original per-scent
  .ocu-cart-line-item-wrapper rows for each bundle group (kept in the DOM,
  just display:none, so OCU's own bookkeeping is undisturbed), and injects one
  combined summary card in their place — same non-invasive pattern as
  ocu-subscription-discount-overlay.js. The combined price uses each real
  line's original_line_price/final_line_price, so once the Shopify automatic
  discount for this bundle is live in Admin, the "was/now" shown here updates
  automatically with no theme change needed. A "Show N items" toggle reveals
  the real per-scent rows (with their own remove/qty controls) without losing
  any functionality — grouping is visual only, never data-lossy.
*/
(function () {
  var WRAPPER_SELECTOR = '.ocu-cart-line-item-wrapper[data-key]';
  var GROUP_CLASS = 'ocu-bundle-group-card';

  var style = document.createElement('style');
  style.textContent =
    '.' + GROUP_CLASS + ' {' +
      'display: flex;' +
      'align-items: flex-start;' +
      'gap: 10px;' +
      'margin: 0 0 0rem;' +
      'border-radius: 8px;' +
      'font-family: var(--ocu-main-font);' +
    '}' +
    '.' + GROUP_CLASS + '__images {' +
      'display: flex;' +
      'flex-shrink: 0;' +
      'width:100px;' +
      'height:100px;' +
    '}' +
    '.' + GROUP_CLASS + '__images img {' +
      'width: 50%;' +
      'height: 40px;' +
      'object-fit: cover;' +
      'border-radius: 50%;' +
      'border: 2px solid #fff;' +
      'box-shadow: 0 0 0 1px #e5e5e5;' +
      'margin-left: -12px;' +
    '}' +
     '.' + GROUP_CLASS + '__images--four img {' +
          'width: 34px;' +
          'height: 34px;' +
        '}' +
    '.' + GROUP_CLASS + '__images img:first-child {' +
      'margin-left: 0;' +
    '}' +
    '.' + GROUP_CLASS + '__info {' +
      'flex: 1 1 140px;' +
      'min-width: 0;' +
    '}' +
    '.' + GROUP_CLASS + '__count {' +
      'margin: 0 0 2px;' +
      'font-size: 11px;' +
      'font-weight: 600;' +
      'text-transform: uppercase;' +
      'letter-spacing: .02em;' +
      'color: var(--ocu-elements-color, #6b6b6b);' +
      'opacity: .75;' +
    '}' +
    '.' + GROUP_CLASS + '__title {' +
      'margin: 0;' +
      'font-weight: 600;' +
      'font-size: 14px;' +
      'color: var(--ocu-elements-color, #1c1d1d);' +
    '}' +
    '.' + GROUP_CLASS + '__scents {' +
      'margin: 2px 0 0;' +
      'font-size: 12px;' +
      'color: var(--ocu-elements-color, #6b6b6b);' +
      'opacity: .8;' +
    '}' +
    '.' + GROUP_CLASS + '__toggle {' +
      'margin: 4px 0 0;' +
      'padding: 0;' +
      'border: 0;' +
      'background: none;' +
      'font-size: 12px;' +
      'text-decoration: underline;' +
      'color: var(--ocu-elements-color, #6b6b6b);' +
      'cursor: pointer;' +
    '}' +
    '.' + GROUP_CLASS + '__price {' +
      'display: block;' +
      'flex-shrink: 0;' +
      'text-align: right;' +
      'font-family: var(--ocu-main-font);' +
    '}' +
    '.' + GROUP_CLASS + '__was {' +
      'display: inline;' +
      'margin-right: 4px;' +
      'text-decoration: line-through;' +
      'vertical-align: top;' +
      'color: var(--ocu-elements-color, #6b6b6b);' +
      'font-size: 14px;' +
    '}' +
    '.' + GROUP_CLASS + '__now {' +
      'display: inline;' +
       'vertical-align: top;' +
      'font-weight: 400;' +
      'font-size: 14px;' +
      'color: var(--ocu-elements-color, #1c1d1d);' +
    '}' +
    '.' + GROUP_CLASS + '__remove {' +
      'flex-shrink: 0;' +
      'border: 0;' +
      'width: 20px;' +
      'height: 20px;' +
      'background: none;' +
      'font-size: 18px;' +
      'line-height: 1;' +
      'color: var(--ocu-elements-color, #6b6b6b);' +
      'cursor: pointer;' +
      'padding: 4px;' +
    '}' +
    '.' + GROUP_CLASS + '-items[hidden] {' +
      'display: none;' +
    '}';
  document.head.appendChild(style);

  function formatMoney(cents) {
    if (window.theme && theme.Currency && typeof theme.Currency.formatMoney === 'function' && theme.settings) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function groupByBundleId(cartItems) {
    var groups = {};
    cartItems.forEach(function (item) {
      var bundleId = item.properties && item.properties['_Bundle Id'];
      if (!bundleId) return;
      (groups[bundleId] = groups[bundleId] || []).push(item);
    });
    // A single real line sharing a bundle id with nothing else isn't worth merging
    Object.keys(groups).forEach(function (id) {
      if (groups[id].length < 2) delete groups[id];
    });
    return groups;
  }

  function removeGroup(keys) {
    // Removes every real line in one atomic /cart/update.js call instead of
    // sequential /cart/change.js calls per key. Sequential calls left the
    // cart briefly sitting at 2-of-3, then 1-of-3 items between requests,
    // and this script's own MutationObserver/cart_changed refresh would
    // re-render the card (or ungroup the last leftover line) against those
    // transient, invalid in-between states, producing the reported glitch.
    var updates = {};
    keys.forEach(function (key) {
      updates[key] = 0;
    });
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates }),
      credentials: 'same-origin',
    })
      .then(function (res) {
        return res.json();
      })
      .then(function (cart) {
        document.dispatchEvent(new CustomEvent('cart:build'));
        window.dispatchEvent(new CustomEvent('cart_changed'));
        verifyDrawerAfterRemove(cart);
      })
      .catch(function (err) {
        console.warn('OCU bundle grouping: remove failed', err);
      });
  }

  function cleanInvalidParentProducts(cart) {
    var invalidKeys = [];
    cart.items.forEach(function (item) {
      var title = item.product_title || item.title;
      if (title.indexOf('Solid Cologne') !== -1 && title.indexOf('Pack') !== -1) {
        invalidKeys.push(item.key);
      }
    });

    if (invalidKeys.length > 0) {
      var updates = {};
      invalidKeys.forEach(function (key) {
        updates[key] = 0;
      });
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates }),
        credentials: 'same-origin',
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (updatedCart) {
          document.dispatchEvent(new CustomEvent('cart:build'));
          window.dispatchEvent(new CustomEvent('cart_changed'));
          return { cleaned: true, cart: updatedCart };
        });
    }
    return Promise.resolve({ cleaned: false, cart: cart });
  }
  // OCU's own drawer occasionally fails to redraw ANY remaining line items
  // after this atomic multi-key removal (confirmed: the real cart is always
  // correct — /cart.js still has the right items/total — but OCU's rendered
  // DOM shows zero .ocu-cart-line-item-wrapper rows, as if the cart were
  // empty of line items, even though recommendations/total/count still show
  // correctly). Closing and reopening the drawer does NOT fix this — it's
  // stuck internal OCU state that only clears on a real page load. So: if
  // the cart still has real items left after a bundle removal, check whether
  // OCU actually rendered them; if not, force a reload as a last resort
  // rather than leave the customer looking at a drawer that appears empty
  // while the real cart still has products in it.
  function verifyDrawerAfterRemove(cart) {
    if (!cart.items.length) return; // nothing left to verify

    setTimeout(function () {
      var renderedKeys = Array.from(document.querySelectorAll(WRAPPER_SELECTOR)).map(function (w) {
        return w.getAttribute('data-key');
      });
      var missing = cart.items.some(function (item) {
        return renderedKeys.indexOf(item.key) === -1;
      });
      if (missing) window.location.reload();
    }, 1200);
  }

  // Builds the card + a sibling container that physically houses the real
  // wrapper elements (moved, not just hidden in place). OCU doesn't
  // necessarily render a bundle's lines contiguously, so anchoring "Show
  // items" on wherever they originally sat scatters them across the drawer —
  // moving them into one container right after the card keeps them together
  // regardless of their original render order.
  function buildGroupBlock(items, wrappers, bundleLabel, bundleIndex, bundleCount) {
    var card = document.createElement('div');
    card.className = GROUP_CLASS;

    var originalTotal = items.reduce(function (sum, i) { return sum + i.original_line_price; }, 0);
    var finalTotal = items.reduce(function (sum, i) { return sum + i.final_line_price; }, 0);
    var scentNames = items
      .map(function (i) { return i.variant_title || i.title; })
      .join(', ');

 var imageCountClass = items.length === 4
  ? GROUP_CLASS + '__images--four'
  : '';

var imagesHtml = items
  .slice(0, 4)
      .map(function (i) {
        return i.image ? '<img src="' + i.image + '" alt="">' : '';
      })
      .join('');

    var savedAmount = originalTotal - finalTotal;
    // Reuses OCU's own markup/classes for the per-line "$X.XX Savings" text
    // (ocu-cart-line-price-saving etc.) so the bundle card's savings label
    // automatically matches OCU's styling instead of a hand-rolled lookalike.
    var savingsHtml = savedAmount > 0
      ? '<div class="ocu-discount-overlay ocu-discount-overlay--savings">' +
          '<p class="ocu-cart-line-price-amount"><span>' + formatMoney(savedAmount) + '</span></p>' + " " +
          '<div class="ocu-cart-line-price-text"><p><span>Savings</span></p></div>' +
        '</div>'
      : '';

    var priceHtml =
      originalTotal > finalTotal
        ? '<span class="' + GROUP_CLASS + '__was">' + formatMoney(originalTotal) + '</span>' +
          '<span class="' + GROUP_CLASS + '__now">' + formatMoney(finalTotal) + '</span>' +
          savingsHtml
        : '<span class="' + GROUP_CLASS + '__now">' + formatMoney(finalTotal) + '</span>';

    // When the same bundle has been added more than once (each add gets its
    // own real per-scent lines and its own _Bundle Id), each instance renders
    // as its own card — otherwise identical-looking cards give no indication
    // there's more than one. Label them "Bundle 1 of 2" etc. so it's explicit
    // rather than making the customer count cards themselves.
    var countLabel = bundleCount > 1 ? '<p class="' + GROUP_CLASS + '__count">Bundle ' + bundleIndex + ' of ' + bundleCount + '</p>' : '';

    card.innerHTML =
     '<div class="' + GROUP_CLASS + '__images ' + imageCountClass + '">' + imagesHtml + '</div>' +
      '<div class="' + GROUP_CLASS + '__info">' +
        countLabel +
        '<p class="' + GROUP_CLASS + '__title"></p>' +
        '<p class="' + GROUP_CLASS + '__scents"></p>' +
        '<button type="button" class="' + GROUP_CLASS + '__toggle">Show ' + items.length + ' items</button>' +
      '</div>' +
      '<div class="' + GROUP_CLASS + '__price">' + priceHtml + '</div>' +
      '<button type="button" class="' + GROUP_CLASS + '__remove" aria-label="Remove bundle"><svg data-v-ca54041a="" data-v-25c3b0fb="" class="ocu-icon"><use data-v-ca54041a="" xmlns:xlink="http://www.w3.org/1999/xlink" xlink:href="#cart_item_remove"></use></svg></button>';

    card.querySelector('.' + GROUP_CLASS + '__title').textContent = bundleLabel;
    card.querySelector('.' + GROUP_CLASS + '__scents').textContent = scentNames;

    var itemsContainer = document.createElement('div');
    itemsContainer.className = GROUP_CLASS + '-items';
    itemsContainer.hidden = true;
    wrappers.forEach(function (w) {
      w.style.display = '';
      itemsContainer.appendChild(w); // moves the real element here, in items order
    });

    var toggleBtn = card.querySelector('.' + GROUP_CLASS + '__toggle');
    toggleBtn.addEventListener('click', function () {
      var willShow = itemsContainer.hidden;
      itemsContainer.hidden = !willShow;
      toggleBtn.textContent = willShow ? 'Hide items' : 'Show ' + wrappers.length + ' items';
    });

    card.querySelector('.' + GROUP_CLASS + '__remove').addEventListener('click', function () {
      removeGroup(items.map(function (i) { return i.key; }));
    });

    return { card: card, itemsContainer: itemsContainer };
  }

  function applyGrouping(cart) {
    var groups = groupByBundleId(cart.items);
    var groupIds = Object.keys(groups);

    // Drop group blocks for bundles that no longer exist in the cart (removed, etc.),
    // releasing any wrappers still parked inside their items container first.
    document.querySelectorAll('.' + GROUP_CLASS + '[data-bundle-id]').forEach(function (card) {
      var bundleId = card.getAttribute('data-bundle-id');
      if (groupIds.indexOf(bundleId) !== -1) return;
      var itemsContainer = card.nextElementSibling;
      if (itemsContainer && itemsContainer.classList.contains(GROUP_CLASS + '-items')) {
        while (itemsContainer.firstChild) {
          itemsContainer.parentNode.insertBefore(itemsContainer.firstChild, itemsContainer);
        }
        itemsContainer.remove();
      }
      card.remove();
    });

    // DOM-order snapshot, used to find each group's true first-rendered member —
    // OCU's render order doesn't necessarily match /cart.js's item order.
    var allWrappers = Array.from(document.querySelectorAll(WRAPPER_SELECTOR));

    // Chronological order (bundle ids are 'bundle-<timestamp>-<random>'), so
    // adding the same bundle multiple times numbers them in the order they
    // were actually added ("Bundle 1 of 2", "Bundle 2 of 2", ...).
    var sortedGroupIds = groupIds.slice().sort(function (a, b) {
      return (parseInt(a.split('-')[1], 10) || 0) - (parseInt(b.split('-')[1], 10) || 0);
    });

    sortedGroupIds.forEach(function (bundleId, groupIndex) {
      var items = groups[bundleId];
      var keys = items.map(function (i) { return i.key; });
      var wrappers = allWrappers.filter(function (w) {
        return keys.indexOf(w.getAttribute('data-key')) !== -1;
      });

      // Previously required every key to have a matching wrapper before
      // rendering anything, which meant a second (or third) instance of the
      // same bundle could silently never appear if OCU's own DOM didn't give
      // this group a full 1:1 wrapper match (e.g. its rows hadn't finished
      // rendering yet). Render with whatever real wrappers exist now instead
      // of bailing entirely — a later refresh corrects it once OCU catches up.
      if (wrappers.length === 0) return; // nothing to attach this group to yet

      var finalTotal = items.reduce(function (sum, i) { return sum + i.final_line_price; }, 0);
      var existing = document.querySelector('.' + GROUP_CLASS + '[data-bundle-id="' + bundleId + '"]');

      // Already grouped with the same total, same "Bundle X of Y" numbering,
      // and every real row still parked inside our items container — nothing
      // to do. The count check matters because adding/removing a bundle
      // changes every other bundle's "of Y" label even when its own total
      // hasn't changed.
      if (
        existing &&
        existing.getAttribute('data-final-total') === String(finalTotal) &&
        existing.getAttribute('data-bundle-count') === String(sortedGroupIds.length)
      ) {
        var currentContainer = existing.nextElementSibling;
        var allParked = currentContainer && wrappers.every(function (w) { return w.parentNode === currentContainer; });
        if (allParked) return;
      }

      // Release this group's wrappers from any previous itemsContainer back
      // into the real list BEFORE computing anchor/parent below. Otherwise,
      // when a group is already rendered and just needs a refresh (e.g. its
      // "Bundle X of Y" label changing because another bundle was removed),
      // its wrappers are still parked inside its OWN old itemsContainer —
      // anchor.parentNode would be that old container, which gets deleted a
      // few lines down, leaving the rebuilt card inserted into a detached
      // node that was removed from the document but still referenced.
      if (existing) {
        var oldContainer = existing.nextElementSibling;
        if (oldContainer && oldContainer.classList.contains(GROUP_CLASS + '-items')) {
          while (oldContainer.firstChild) {
            oldContainer.parentNode.insertBefore(oldContainer.firstChild, oldContainer);
          }
          oldContainer.remove();
        }
        existing.remove();
      }

      var anchor = wrappers[0]; // first-in-DOM-order member of this group, now correctly parented in the real list
      var parent = anchor.parentNode;
      // anchor is by definition the earliest group member in DOM order, so
      // whatever precedes it is guaranteed NOT part of this group — a safe
      // reference point that survives the wrapper-moving below (anchor.nextSibling
      // is not safe: it could itself be another member of this same group).
      var precedingSibling = anchor.previousSibling;

      var bundleLabel = (items[0].properties && items[0].properties['_Bundle']) || 'Bundle';
      var block = buildGroupBlock(items, wrappers, bundleLabel, groupIndex + 1, sortedGroupIds.length);
      block.card.setAttribute('data-bundle-id', bundleId);
      block.card.setAttribute('data-final-total', String(finalTotal));
      block.card.setAttribute('data-bundle-count', String(sortedGroupIds.length));

      // wrappers (including anchor) have already been moved into
      // block.itemsContainer by buildGroupBlock. Re-insert the block right
      // after precedingSibling (or at the very start if anchor had none).
      var insertBeforeRef = precedingSibling ? precedingSibling.nextSibling : parent.firstChild;
      parent.insertBefore(block.card, insertBeforeRef);
      parent.insertBefore(block.itemsContainer, insertBeforeRef);
    });
  }

  var lastCart = null;

  function refresh() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        return cleanInvalidParentProducts(cart).then(function (result) {
          if (result.cleaned) {
            return refresh();
          }
          lastCart = result.cart;
          applyGrouping(result.cart);
          return result.cart;
        });
      })
      .catch(function (err) { console.warn('OCU bundle grouping: cart fetch failed', err); });
  }

  var debounceTimer;
  function scheduleRefresh(immediate) {
    clearTimeout(debounceTimer);
    if (immediate === true) {
      refresh();
    } else {
      debounceTimer = setTimeout(refresh, 100);
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    var observer = new MutationObserver(function () {
      if (lastCart && document.querySelector(WRAPPER_SELECTOR)) {
        applyGrouping(lastCart);
      }
      scheduleRefresh();
    });
    observer.observe(document.body, { childList: true, subtree: true });

        // Final Safety Lock: Block checkout if invalid parent bundle exists
    document.addEventListener('click', function (e) {
      var checkoutBtn = e.target.closest('.cart__checkout');
      if (!checkoutBtn) return;

      var hasInvalid = false;
      if (lastCart && lastCart.items) {
        hasInvalid = lastCart.items.some(function (item) {
          var title = item.product_title || item.title;
          return title.indexOf('Solid Cologne') !== -1 && title.indexOf('Pack') !== -1;
        });
      }

      if (hasInvalid) {
        e.preventDefault();
        e.stopImmediatePropagation();

        // Force a cleanup and alert the user
        cleanInvalidParentProducts(lastCart).then(function () {
          alert('Your cart is being updated to remove invalid bundle items. Please wait a moment and try again.');
        });
      }
    });


    document.addEventListener('cart:build', function () { scheduleRefresh(true); });
    window.addEventListener('cart_changed', function () { scheduleRefresh(true); });
    document.addEventListener('ajaxProduct:added', function () { scheduleRefresh(true); });

    scheduleRefresh(true);
  });
})();
